/**
 * CartAnalytics — unified dispatcher for cart-drawer events to GA4
 * with optional Sentry breadcrumb mirroring for error/state debugging.
 *
 * Every event automatically gets:
 *   - cart_token:          first 8 chars of the cart cookie
 *   - cart_item_count:     header cart-count element, fallback to drawer DOM count
 *   - cart_total_cents:    document.body[data-cart-total-cents], seeded by cart-drawer.liquid
 *
 * Audit guide for setting up these events in GA4:
 *   See published Lighthouse report (minicart_v2_ga4_audit) for dashboard
 *   recipes, custom dimensions, and recommended Looker Studio joins.
 */
const CartAnalytics = {
  _baseProps() {
    let cartToken = '';
    try {
      const m = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
      cartToken = m ? m[1].slice(0, 8) : '';
    } catch (_e) { /* fine */ }
    // cart_item_count — header <cart-count> renders empty/hidden until /cart.js
    // resolves (theme.js:hydrateCartCount), so reading it pre-hydration would
    // ship `0` for visitors whose cart actually has items. Prefer the drawer
    // DOM (only present when drawer has been loaded) and only fall back to a
    // header cart-count that's been hydrated (not `[hidden]` and has visible
    // text). Emit `undefined` when unknown so dashboards can distinguish
    // missing-data from genuine zero.
    let itemCount;
    try {
      const drawerLines = document.querySelectorAll('cart-items li.cart-item').length;
      if (drawerLines > 0) {
        itemCount = drawerLines;
      } else {
        const countEl = document.querySelector('cart-count:not([hidden])');
        if (countEl) {
          const txt = countEl.getAttribute('data-cart-item-count') || countEl.textContent || '';
          const parsed = parseInt(String(txt).replace(/[^0-9]/g, ''), 10);
          if (Number.isFinite(parsed)) itemCount = parsed;
        }
      }
    } catch (_e) { /* fine */ }
    // cart_total_cents — read from <cart-shipping-estimate data-cart-total-cents>
    // first (server-rendered, lives inside the cart-drawer subtree, its
    // attributes survive Section Rendering API innerHTML swaps because HTML
    // parsing preserves attributes on parsed elements). Fall back to the body
    // attribute which the cart:updated DOM handler in cart-drawer.liquid keeps
    // in sync for post-update events. Omit (leave undefined) when unknown —
    // analytics should distinguish missing-data from genuine zero.
    let totalCents;
    try {
      const estEl = document.querySelector('cart-shipping-estimate[data-cart-total-cents]');
      const fromEst = estEl && estEl.getAttribute('data-cart-total-cents');
      const bodyRaw = document.body.dataset.cartTotalCents;
      const raw = fromEst != null && fromEst !== '' ? fromEst : bodyRaw;
      const parsed = parseInt(raw || '', 10);
      if (Number.isFinite(parsed) && parsed >= 0) totalCents = parsed;
    } catch (_e) { /* fine */ }
    return {
      cart_token: cartToken,
      cart_item_count: itemCount,
      cart_total_cents: totalCents,
    };
  },
  push(eventName, extra) {
    const props = Object.assign({}, this._baseProps(), extra || {});
    const w = /** @type {any} */ (window);
    // Each provider in its own try/catch so a thrown call in one doesn't starve
    // the others. When a provider throws, breadcrumb the failure via Sentry so
    // we can see provider outages without depending on a separate captured
    // exception in the same session. The Sentry breadcrumb path is last and
    // genuinely best-effort — there's no floor below it.
    try {
      if (w.GA4CustomEvents && typeof w.GA4CustomEvents.pushCustomEvent === 'function') {
        w.GA4CustomEvents.pushCustomEvent(eventName, props);
      }
    } catch (err) {
      this.breadcrumb('cart_analytics_provider_threw', {
        provider: 'ga4',
        event: eventName,
        error: err && err.message,
      }, 'warning');
    }
    try {
      if (w.Sentry && typeof w.Sentry.addBreadcrumb === 'function') {
        w.Sentry.addBreadcrumb({
          category: 'cart',
          type: 'info',
          level: 'info',
          message: eventName,
          data: props,
        });
      }
    } catch (_e) { /* nowhere to log if Sentry itself throws */ }
  },
  breadcrumb(message, data, level) {
    const w = /** @type {any} */ (window);
    try {
      if (w.Sentry && typeof w.Sentry.addBreadcrumb === 'function') {
        w.Sentry.addBreadcrumb({
          category: 'cart',
          type: 'info',
          level: level || 'warning',
          message: message,
          data: Object.assign({}, data || {}),
        });
      }
    } catch (_e) { /* fine */ }
  },
};
/** @type {any} */ (window).CartAnalytics = CartAnalytics;

class CartDrawer extends DrawerComponent {
  constructor() {
    super();
    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    this.getSectionToRenderListener = this.getSectionToRender.bind(this);
    // Lazy loading: track if full content has been loaded
    this.contentLoaded = !this.hasAttribute('data-lazy');
    this.isLoadingContent = false;
    // Concurrency guards: cache the in-flight initial-load promise so concurrent show() calls
    // dedupe; serialize refresh fetches via a chained promise so out-of-order responses can't
    // overwrite the latest swap.
    this._loadPromise = null;
    this._refreshChain = Promise.resolve();
  }

  get requiresBodyAppended() {
    return false;
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  hide() {
    super.hide();
    CartAnalytics.push('cart_drawer_close', {});
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.addEventListener('cart:refresh', this.onCartRefreshListener);

    // Delegated listener for primary CTA click — survives Section Rendering API
    // swaps because it's attached to the host element, not the inner button.
    this.addEventListener('click', (event) => {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      const checkoutBtn = target.closest('button.cart-drawer__checkout');
      if (checkoutBtn) {
        // cart_total_cents auto-attached via CartAnalytics._baseProps()
        // NOT `source` — that is a GA4 reserved parameter. Sending it on an
        // event overrides the session's traffic source, and because no
        // `medium` accompanied it GA4 could not channel-classify the session,
        // dropping it to Unassigned. Since this fires on the checkout click it
        // hit buyers specifically: 1,859 sessions and $52,409 attributed to a
        // source named `secure_checkout_primary` (#1581).
        CartAnalytics.push('cart_checkout_click', {
          cta_source: 'secure_checkout_primary',
        });
        return;
      }
      const expressBtn = target.closest('.cart-drawer__express button, .cart-drawer__express [role="button"], .shopify-payment-button button');
      if (expressBtn) {
        CartAnalytics.push('cart_express_checkout_click', {
          provider_class: (expressBtn.className || '').slice(0, 60),
        });
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.removeEventListener('cart:refresh', this.onCartRefreshListener);
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }

  async show(focusElement = null, animate = true) {
    // Lazy loading: fetch content on first open if not already loaded. Concurrent show()s
    // (e.g. user double-clicks the cart icon) await the same promise instead of starting
    // overlapping fetches.
    if (!this.contentLoaded && !Shopify.designMode) {
      if (!this._loadPromise) {
        this._loadPromise = this.loadContent().finally(() => {
          this._loadPromise = null;
        });
      }
      await this._loadPromise;
    }
    super.show(focusElement, animate);

    if (this.open && !Shopify.designMode) {
      FoxTheme.a11y.trapFocus(this, this.focusElement);
    }

    const emptyState = document.getElementById(`CartDrawerEmpty-${this.sectionId}`);
    CartAnalytics.push('cart_drawer_open', {
      is_empty: emptyState ? !emptyState.classList.contains('hidden') : false,
    });
  }

  async loadContent() {
    this.isLoadingContent = true;
    const cartId = `CartDrawer-${this.sectionId}`;
    const cartElement = document.getElementById(cartId);
    if (!cartElement) {
      this.isLoadingContent = false;
      return;
    }

    const skeleton = cartElement.querySelector('.cart-drawer-skeleton');

    try {
      const response = await fetch(`${FoxTheme.routes.root_url}?section_id=${this.sectionId}`);
      // Shopify returns the storefront error page on 4xx/5xx with status HTML in the body, so
      // `response.text()` resolves and the DOMParser path silently produces no match for cartId.
      // Without this check the user would see the pulsing skeleton forever (`contentLoaded`
      // stays false; subsequent show()s retry the same failing fetch). Throw to route into the
      // catch block, which surfaces a localized error in the skeleton slot.
      if (!response.ok) {
        throw new Error(`cart-drawer fetch ${response.status}`);
      }
      const responseText = await response.text();

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(responseText, 'text/html');

      const newCartContent = parsedHTML.getElementById(cartId)?.innerHTML;
      if (!newCartContent) {
        throw new Error('cart-drawer response missing expected DOM');
      }
      cartElement.innerHTML = newCartContent;
      this.contentLoaded = true;
      // Re-bind click handlers to new close buttons after innerHTML replacement
      this.rebindControlListeners();
      // Shopify dynamic checkout buttons (Shop Pay / Apple Pay / Google Pay /
      // PayPal) are hydrated from `<div class="shopify-payment-button">`
      // placeholders by an external SDK that scans the DOM on page load.
      // When we inject the drawer via Section Rendering API, the placeholder
      // arrives AFTER the SDK has already scanned — leaving an empty
      // placeholder div with no buttons. Manually re-init.
      this.initPaymentButton();
      // Sync the persistent <shop-pay-button>'s variants attribute to current cart.
      this.updateShopPayVariants();
    } catch (error) {
      console.error('Error loading cart drawer:', error);
      if (skeleton) {
        // Localized message rendered Liquid-side onto <cart-drawer data-load-error-message>.
        // Falls back to a static English string only if the attribute is somehow missing
        // (e.g. design-mode preview before the data attr was added). Use textContent so any
        // HTML-special chars in the translation render as plain text rather than markup.
        const message = this.dataset.loadErrorMessage || 'Could not load cart. Please refresh the page.';
        const errorEl = document.createElement('p');
        errorEl.className = 'text-center p-4';
        errorEl.textContent = message;
        // The item was already added (POST /cart/add succeeded before this fetch);
        // give the shopper a working path to checkout instead of dead-ending in the
        // drawer. Links to the full cart page, which renders server-side independent
        // of the failing Section Rendering fetch.
        const cartLink = document.createElement('a');
        cartLink.className = 'btn btn--primary w-full';
        cartLink.href = window.FoxTheme?.routes?.cart_url || '/cart';
        cartLink.textContent = this.dataset.viewCartLabel || 'View cart & checkout';
        const wrap = document.createElement('div');
        wrap.className = 'flex flex-col items-center gap-4 p-4';
        wrap.append(errorEl, cartLink);
        skeleton.replaceChildren(wrap);
      }
    } finally {
      this.isLoadingContent = false;
    }
  }

  async onCartRefresh(event) {
    // `event.detail` is null for `cart:refresh` events dispatched without an init dict —
    // algolia-search-page.js and algolia-collection-page.js fire `new CustomEvent('cart:refresh')`
    // with no detail object, which is the most-trafficked ATC path. Optional-chain so we don't
    // throw and silently break drawer refresh on those pages.
    const wantsOpen = event.detail?.open === true;

    // Wait for any in-flight initial loadContent() so we don't race with its swap.
    if (this._loadPromise) await this._loadPromise;

    // Serialize concurrent refreshes via a promise chain. Rapid cart mutations (double-ATC,
    // quantity step, etc.) fire `cart:refresh` multiple times in the same tick — without
    // serialization the slower response can overwrite the faster one and the user sees stale
    // state. Chaining guarantees each fetch+swap completes before the next starts.
    this._refreshChain = this._refreshChain.then(async () => {
      const cartId = `CartDrawer-${this.sectionId}`;
      const cartElement = document.getElementById(cartId);
      if (!cartElement) return;

      try {
        const response = await fetch(`${FoxTheme.routes.root_url}?section_id=${this.sectionId}`);
        const responseText = await response.text();

        const parser = new DOMParser();
        const parsedHTML = parser.parseFromString(responseText, 'text/html');

        const newCartContent = parsedHTML.getElementById(cartId).innerHTML;
        cartElement.innerHTML = newCartContent;
        this.rebindControlListeners();
        this.initPaymentButton();
        this.updateShopPayVariants();

        if (wantsOpen) this.show();
      } catch (error) {
        console.error('Error refreshing cart:', error);
      }
    });

    await this._refreshChain;
  }

  /**
   * Re-bind click listeners to control elements after DOM replacement.
   * Scoped to `this.contains(button)` so we only touch buttons INSIDE the drawer (close
   * buttons, etc.). `this.controls` includes external openers — header cart link and mobile
   * sticky bar — whose listeners are managed by ModalComponent.connectedCallback via an
   * AbortController. Cloning those nodes would orphan the AbortController-bound listener and
   * leak handlers across the page lifetime.
   */
  rebindControlListeners() {
    this.controls.forEach((button) => {
      if (!this.contains(button)) return;
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);
      newButton.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  /**
   * Update the persistent <shop-pay-button> web component's `variants`
   * attribute whenever the cart changes. The component lives OUTSIDE the
   * Section Rendering API swap zone (sibling of #CartDrawer-cart-drawer)
   * so server-rendered variants get stale on cart add/remove/quantity
   * changes — we re-sync from /cart.js after each cart mutation.
   *
   * The web component watches its `variants` attribute and re-renders
   * when it changes. Empty cart → empty variants string → component
   * silently no-ops (the persistent block is also hidden via CSS
   * :has(#CartDrawerEmpty-cart-drawer:not(.hidden))).
   */
  async updateShopPayVariants() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.ok ? r.json() : null);
      if (!cart || !Array.isArray(cart.items)) return;

      const persistentExtras = document.querySelector('.cart-drawer__persistent-extras');
      if (!persistentExtras) return;

      // This block lives outside the drawer's swap zone (so hydrated payment
      // iframes survive cart updates), so <shop-pay-button> may not exist yet
      // even mid-session — create it on demand rather than assuming the
      // Liquid template already rendered one.
      if (cart.items.length === 0) {
        persistentExtras.classList.add('hidden');
        return;
      }
      persistentExtras.classList.remove('hidden');

      const variants = cart.items.map(i => `${i.variant_id}:${i.quantity}`).join(',');
      let btn = persistentExtras.querySelector('[data-cart-drawer-shop-pay]');
      if (!btn) {
        if (!window.Shopify?.shop) return;
        btn = document.createElement('shop-pay-button');
        btn.setAttribute('store-url', `https://${window.Shopify.shop}`);
        btn.setAttribute('data-cart-drawer-shop-pay', '');
        persistentExtras.querySelector('.cart-drawer__express').appendChild(btn);
      }
      btn.setAttribute('variants', variants);
    } catch (_e) { /* best effort — express path is secondary, primary CTA still works */ }
  }

  /**
   * Re-hydrate Shopify dynamic checkout buttons after Section Rendering API
   * injects the drawer's HTML. The buttons are empty `<div
   * class="shopify-payment-button">` placeholders that Shopify's external
   * payment-button SDK scans for and replaces with iframes. The SDK only
   * scans the DOM once on page load — anything injected later (like our
   * lazy-fetched drawer) sits there empty unless we manually trigger init.
   *
   * Shopify exposes two re-init paths depending on which SDK version loaded:
   *   1. `Shopify.PaymentButton.init()` — legacy theme support
   *   2. `shopify:section:load` event — modern theme editor pattern that
   *      multiple Shopify scripts subscribe to for re-scan
   *
   * Fire both — whichever the live store has wired will pick it up. They're
   * idempotent on already-hydrated buttons.
   */
  initPaymentButton() {
    const w = /** @type {any} */ (window);
    try {
      if (w.Shopify && w.Shopify.PaymentButton && typeof w.Shopify.PaymentButton.init === 'function') {
        w.Shopify.PaymentButton.init();
      }
    } catch (_e) { /* swallow — checkout still works via primary CTA */ }
    try {
      // Modern theme-editor event. Some Shopify SDK builds listen for this.
      const sectionEl = document.getElementById(`shopify-section-${this.sectionId}`);
      if (sectionEl) {
        document.dispatchEvent(new CustomEvent('shopify:section:load', {
          detail: { sectionId: this.sectionId, load: true, sectionEl },
        }));
      }
    } catch (_e) { /* fine */ }
  }
}
customElements.define('cart-drawer', CartDrawer);

class CartAddonModal extends ModalComponent {
  constructor() {
    super();
  }

  /**
   * To avoid lost focus input elements
   */
  get focusElement() {
    return this.querySelector('input, textarea, select') || this.querySelector('button');
  }
}
customElements.define('cart-addon-modal', CartAddonModal);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );

    this.cartItemProducts = this.querySelectorAll('.cart-item__product');
    this.cartItemQuantitys = this.querySelectorAll('.cart-item__quantity');

    const loadTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const template = element.querySelector('template');
        const templateContent = template && document.importNode(template.content, true);
        const parent = element.querySelector(parentSelector);

        if (parent && template && !parent.querySelector('.template-content')) {
          const contentWrapper = document.createElement('div');
          contentWrapper.classList.add('template-content');
          contentWrapper.appendChild(templateContent);
          parent.appendChild(contentWrapper);
        }
      });
    };

    const removeTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const parent = element.querySelector(parentSelector);
        const templateContent = parent?.querySelector('.template-content');

        if (templateContent) {
          parent.removeChild(templateContent);
        }
      });
    };

    const handleTabletMatch = () => {
      loadTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      removeTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const handleTabletUnmatch = () => {
      removeTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      loadTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const mqlTablet = window.matchMedia(FoxTheme.config?.mediaQueryTablet ?? 'screen and (max-width: 1023px)');
    FoxTheme.config.mqlTablet = mqlTablet.matches;

    if (FoxTheme.config.mqlTablet) {
      handleTabletMatch();
    } else {
      handleTabletUnmatch();
    }

    mqlTablet.onchange = (event) => {
      if (event.matches) {
        handleTabletMatch();
      } else {
        FoxTheme.config.mqlTablet = false;
        handleTabletUnmatch();
      }
    };

    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
  }

  cartUpdateUnsubscriber = undefined;

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target
    );
  }

  onCartError(errors, target) {
    if (target) {
      this.hideLoader(target.getAttribute('data-index'));
      this.setValidity(target, errors);
      return;
    } else {
      window.location.href = FoxTheme.routes.cart_url;
    }
    alert(errors);
  }

  setValidity(target, message) {
    target.setCustomValidity(message);
    target.reportValidity();
    target.value = target.defaultValue;
    target.select();
  }

  onCartUpdate(event) {
    if (event.cart.errors) {
      this.onCartError(event.cart.errors, event.target);
      return;
    }

    const sectionId = FoxTheme.utils.getSectionId(this);
    if (!sectionId || !event.cart.sections?.[sectionId]) return;
    const sectionToRender = new DOMParser().parseFromString(event.cart.sections[sectionId], 'text/html');

    const cartDrawer = document.querySelector(`#CartDrawer-${sectionId}`);
    const cartDrawerBody = document.querySelector(`#CartDrawerBody-${sectionId}`);
    const cartDrawerFooter = document.querySelector(`#CartDrawerFooter-${sectionId}`);
    const cartDrawerEmpty = document.querySelector(`#CartDrawerEmpty-${sectionId}`);
    if (cartDrawer) {
      const cartDrawerBodyUpdate = sectionToRender.querySelector(`#CartDrawerBody-${sectionId}`);
      const cartDrawerFooterUpdate = sectionToRender.querySelector(`#CartDrawerFooter-${sectionId}`);
      const cartDrawerEmptyUpdate = sectionToRender.querySelector(`#CartDrawerEmpty-${sectionId}`);

      if (cartDrawerBodyUpdate) {
        cartDrawerBody.innerHTML = cartDrawerBodyUpdate.innerHTML;
      }
      if (cartDrawerFooterUpdate) {
        cartDrawerFooter.innerHTML = cartDrawerFooterUpdate.innerHTML;
      }

      if (cartDrawerEmptyUpdate) {
        cartDrawerEmpty.innerHTML = cartDrawerEmptyUpdate.innerHTML;
      }

      if (event.cart.item_count > 0) {
        cartDrawerBody.classList.remove('hidden');
        cartDrawerFooter.classList.remove('hidden');
        cartDrawerEmpty.classList.add('hidden');
      } else {
        cartDrawerBody.classList.add('hidden');
        cartDrawerFooter.classList.add('hidden');
        cartDrawerEmpty.classList.remove('hidden');
      }
    }

    const mainCart = document.querySelector(`#MainCart-${sectionId}`);
    if (mainCart) {
      const updatedElement = sectionToRender.querySelector(`#MainCart-${sectionId}`);
      if (updatedElement) {
        mainCart.innerHTML = updatedElement.innerHTML;
        if (event.cart.item_count > 0) {
          mainCart.closest('.cart').classList.remove('is-empty');
        } else {
          mainCart.closest('.cart').classList.add('is-empty');
        }
      } else {
        mainCart.closest('.cart').classList.add('is-empty');
        mainCart.remove();
      }
    }

    const lineItem =
      document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);

    if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
      FoxTheme.a11y.trapFocus(mainCart || cartDrawer, lineItem.querySelector(`[name="${event.name}"]`));
    } else if (event.cart.item_count === 0) {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('a'))
        : FoxTheme.a11y.trapFocus(document.querySelector('.cart__empty'), document.querySelector('a'));
    } else {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('.cart-item__title'))
        : FoxTheme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart-item__title'));
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: event.cart,
        },
      })
    );
  }

  updateQuantity(line, quantity, name, target) {
    this.showLoader(line);

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    // Re-snapshot partial-backorder properties for the new qty (issue #1346).
    // ATC-time snapshot goes stale once the customer edits qty in cart.
    //
    // Shopify's /cart/change.js REPLACES the line's properties hash when a
    // `properties` key is included — keys not supplied are removed. When
    // `properties` is omitted entirely, existing properties are preserved.
    //
    // Strategy:
    //   - If the line has no backorder snapshot today AND the new qty doesn't
    //     warrant one, omit `properties` and skip the /cart.js GET entirely
    //     (single POST, the common case).
    //   - Otherwise we must set or clear the snapshot keys. Fetch the line's
    //     existing properties first so unrelated keys (FoxKit offer, gift-card
    //     recipient fields, etc.) survive the replace.
    //
    // `data-has-backorder-prop` is rendered by cart-drawer.liquid / main-cart.liquid
    // based on the same `is_partial_backorder` flag that drives the amber notice.
    const lineEl = document.getElementById(`CartDrawer-Item-${line}`) || document.getElementById(`CartItem-${line}`);
    const invQty = lineEl ? parseInt(lineEl.dataset.inventoryQuantity, 10) : NaN;
    const invPolicy = lineEl ? lineEl.dataset.inventoryPolicy : '';
    const hasBackorderProp = lineEl?.dataset.hasBackorderProp === 'true';
    const shouldHaveBackorderProps =
      invPolicy === 'continue' &&
      Number.isFinite(invQty) &&
      invQty > 0 &&
      quantity > invQty;
    const needsPropertiesTouch = shouldHaveBackorderProps || hasBackorderProp;

    const buildChangeBody = (existingProperties) => {
      const payload = { line, quantity, sections: sectionsToBundle };
      if (needsPropertiesTouch) {
        const properties = { ...existingProperties };
        if (shouldHaveBackorderProps) {
          properties._inventory_at_add = String(invQty);
          properties._qty_backordered_at_add = String(quantity - invQty);
        } else {
          // Stale snapshot — empty string tells Shopify to drop the key.
          properties._inventory_at_add = '';
          properties._qty_backordered_at_add = '';
        }
        payload.properties = properties;
      }
      return JSON.stringify(payload);
    };

    const existingPropsPromise = needsPropertiesTouch
      ? fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })
          .then((response) => response.json())
          .then((cartJson) => cartJson.items?.[parseInt(line, 10) - 1]?.properties || {})
      : Promise.resolve({});

    existingPropsPromise
      .then((existingProperties) => {
        const body = buildChangeBody(existingProperties);
        return fetch(`${FoxTheme.routes.cart_change_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } });
      })
      .then((response) => response.json())
      .then((parsedState) => {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsedState, target, line, name });
      })
      .catch((error) => {
        console.error('Cart update error:', error);
        // onCartUpdate's success path clears the loader implicitly, by
        // replacing the line-item markup wholesale with fresh server HTML.
        // A network/parse failure never reaches that replacement, so
        // without this the spinner showLoader() started above never stops.
        this.hideLoader(line);
        alert(FoxTheme.cartStrings.error);
      });
  }

  showLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.add('btn--loading');
      });
    }
  }

  hideLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.remove('btn--loading');
      });
    }
  }
}
customElements.define('cart-items', CartItems);

class CartRemoveItem extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      // Mini-cart v2 (audit finding 3.8): stage the removal with a 5-second
      // Undo window. findCartToast scopes lookup to the line's container so
      // drawer flow targets the drawer's toast and cart-page flow targets
      // the cart-page toast — both elements share the <cart-drawer-toast>
      // custom element, only positioning + container differ.
      const cartItems = this.closest('cart-items');
      const lineEl = this.closest('[data-cart-line]') || this.closest('li.cart-item') || this.closest('.cart-item');
      const titleEl = lineEl && lineEl.querySelector('.cart-item__title');
      const title = titleEl ? titleEl.textContent.trim() : 'Item';
      const toast = findCartToast(lineEl);

      if (toast && lineEl && cartItems) {
        toast.stageRemoval({
          line: lineEl,
          title,
          onCommit: () => cartItems.updateQuantity(this.dataset.index, 0),
        });
        return;
      }
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-item', CartRemoveItem, { extends: 'a' });

/**
 * findCartToast — pick the <cart-drawer-toast> in the same surface as `el`.
 *
 * <cart-drawer-toast> renders twice on the cart page: once inside the
 * (hidden) cart-drawer and once at the top of <main-cart>. document-wide
 * querySelector would always return the first match (the drawer's), which
 * is invisible while the drawer is closed — so move-to-wishlist and remove
 * Undo flashed silently on the cart page. Scoping by closest('cart-drawer')
 * → closest('.cart') keeps both flows visible.
 */
function findCartToast(el) {
  if (!el) return document.querySelector('cart-drawer-toast');
  return (
    el.closest('cart-drawer')?.querySelector('cart-drawer-toast') ||
    el.closest('.cart')?.querySelector('cart-drawer-toast') ||
    document.querySelector('cart-drawer-toast')
  );
}

/**
 * CartMoveToWishlist - Move cart item to wishlist
 * Adds item to WishlistKing with exact variant, then removes from cart.
 */
class CartMoveToWishlist extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      this.moveToWishlist();
    });
  }

  async moveToWishlist() {
    const productHandle = this.dataset.handle;
    const variantId = this.dataset.variantId;
    const index = this.dataset.index;

    if (!productHandle || !variantId) {
      console.warn('[CartMoveToWishlist] Missing product handle or variant ID');
      CartAnalytics.breadcrumb('cart_move_to_wishlist_missing_params', { productHandle, variantId });
      return;
    }

    CartAnalytics.push('cart_move_to_wishlist_clicked', {
      handle: productHandle,
      variant_id: variantId,
    });

    // Show loading state
    this.classList.add('is-loading');
    this.setAttribute('disabled', '');

    try {
      // Wait for WishlistKing if not yet available
      if (!window.WishlistKing) {
        await this.waitForWishlistKing();
      }
      if (!window.WishlistKing) {
        throw new Error('Wishlist is not available');
      }

      // Add to wishlist with exact variant
      await window.WishlistKing.addWishlistItem({
        productHandle,
        variantId: parseInt(variantId, 10),
      });

      // Dispatch wishlist updated event for header count and button state sync
      document.dispatchEvent(
        new CustomEvent('wishlist:updated', {
          detail: { source: 'cart-move-to-wishlist' },
        })
      );

      // Mini-cart v2 (audit finding 3.10): keep the cart drawer open and
      // surface an inline toast instead of swapping to the wishlist drawer.
      // The wishlist drawer can still be opened from the header heart icon.
      // On the cart page, target the cart-page toast (visible) rather than
      // the drawer's toast (hidden inside the closed drawer).
      const toast = findCartToast(this);
      if (toast) {
        toast.showSavedToast();
      }

      // Remove from cart (reuse CartItems.updateQuantity)
      const cartItems = this.closest('cart-items');
      if (cartItems) {
        cartItems.updateQuantity(index, 0);
      }
    } catch (err) {
      console.error('[CartMoveToWishlist] Failed:', err);
      CartAnalytics.breadcrumb('cart_move_to_wishlist_failed', {
        error: err && err.message,
      }, 'error');
      this.classList.remove('is-loading');
      this.removeAttribute('disabled');
      // Don't remove from cart on failure
    }
  }

  waitForWishlistKing() {
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max
      const check = () => {
        if (window.WishlistKing || ++attempts >= maxAttempts) {
          resolve();
        } else {
          setTimeout(check, 100);
        }
      };
      check();
    });
  }
}
customElements.define('cart-move-to-wishlist', CartMoveToWishlist);

/**
 * CartDrawerToast — Inline toast for cart-drawer interactions.
 * Owns two surfaces:
 *   - "Saved to wishlist" success message (mini-cart v2 finding 3.10)
 *   - Staged-removal toast with 5-second Undo timer (finding 3.8)
 * Markup is rendered server-side in cart-drawer.liquid; this class is the
 * controller. Only one staged removal can be active at a time.
 */
class CartDrawerToast extends HTMLElement {
  constructor() {
    super();
    this.STAGE_DURATION_MS = 5000;
    this._stagedLine = null;
    this._commitTimeout = null;
    this._tickInterval = null;
    this._currentMode = null;

    this._dom = {
      saved: this.querySelector('[data-toast-saved]'),
      removed: this.querySelector('[data-toast-removed]'),
      removedTitle: this.querySelector('[data-toast-removed-title]'),
      removedTimer: this.querySelector('[data-toast-removed-timer]'),
      undoBtn: this.querySelector('[data-toast-undo]'),
    };

    if (this._dom.undoBtn) {
      this._dom.undoBtn.addEventListener('click', (event) => {
        event.preventDefault();
        this._undoRemoval();
      });
    }
  }

  disconnectedCallback() {
    // CRITICAL: when the cart-drawer is re-rendered by Section Rendering API
    // (cart:refresh handler swaps innerHTML), this element gets detached.
    // The 5s commit timer would otherwise keep running with a stale
    // _stagedLine + stale dataset.index, eventually firing
    // cartItems.updateQuantity(staleIndex, 0) against the detached cart and
    // silently removing whatever item NOW occupies that index.
    //
    // Cancel timers and abort the staged removal. The user's original Remove
    // click is treated as not-yet-committed; the new (post-refresh) drawer
    // shows the item still in cart. Better to lose the staged removal than
    // to silently delete the wrong product.
    if (this._stagedLine || this._commitTimeout) {
      CartAnalytics.breadcrumb('cart_remove_stage_orphaned_on_rerender', {
        product_title: this._stagedTitle || '',
        variant_id: this._stagedVariantId || '',
      }, 'warning');
    }
    this._cancelStaged();
  }

  showSavedToast() {
    this._show('saved');
    setTimeout(() => {
      if (this._currentMode === 'saved') this._hide();
    }, 3000);
  }

  stageRemoval({ line, title, onCommit }) {
    if (!line || typeof onCommit !== 'function') return;
    this._cancelStaged();

    CartAnalytics.push('cart_item_remove_staged', {
      product_title: title,
      variant_id: line.dataset.variantId || '',
      handle: line.dataset.handle || '',
    });
    this._stagedTitle = title;
    this._stagedVariantId = line.dataset.variantId || '';

    this._stagedLine = line;
    this._stagedCommit = onCommit;
    line.classList.add('is-staged-remove');

    // Two undo surfaces, picked by which toast instance this is:
    //   - Drawer toast: injects an inline-undo strip into the staged line.
    //     The strip's CSS lives scoped under .cart-drawer (cart-drawer.liquid),
    //     so it only renders correctly inside the drawer.
    //   - Cart-page toast: hides the staged row entirely (.cart .cart-item
    //     .is-staged-remove { display: none } in cart.css) and surfaces Undo
    //     in the top toast body ([data-toast-removed]). The cart page has
    //     room to show a fixed-position toast; the drawer doesn't.
    const useTopToast = this.classList.contains('cart-drawer__toast--page');
    let timerEl;

    if (useTopToast) {
      if (this._dom.removedTitle) this._dom.removedTitle.textContent = title;
      if (this._dom.removedTimer) this._dom.removedTimer.textContent = '5s';
      this._show('removed');
      timerEl = this._dom.removedTimer;
    } else {
    // Inject the undo strip IN-PLACE on the line item so the undo prompt
    // appears where the removed product was, not in a global top banner.
    // The is-staged-remove class hides all other children of the cart-item
    // (via CSS in cart-drawer.liquid); only this injected strip shows.
    const undoStrip = document.createElement('div');
    undoStrip.className = 'cart-item__inline-undo';
    undoStrip.setAttribute('role', 'status');
    undoStrip.setAttribute('aria-live', 'polite');
    const titleSafe = this._escapeHtml(title);
    undoStrip.innerHTML =
      '<span class="cart-item__inline-undo-icon" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
          '<polyline points="3 6 5 6 21 6"/>' +
          '<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>' +
        '</svg>' +
      '</span>' +
      '<span class="cart-item__inline-undo-text">' +
        '<strong>' + titleSafe + '</strong> removed' +
      '</span>' +
      '<span class="cart-item__inline-undo-timer" data-inline-undo-timer aria-hidden="true">5s</span>' +
      '<button type="button" class="cart-item__inline-undo-btn" data-inline-undo>Undo</button>';
    undoStrip.querySelector('[data-inline-undo]').addEventListener('click', (event) => {
      event.preventDefault();
      this._undoRemoval();
    });
    line.appendChild(undoStrip);
    this._inlineUndoStrip = undoStrip;
    timerEl = undoStrip.querySelector('[data-inline-undo-timer]');
    }

    let remaining = Math.round(this.STAGE_DURATION_MS / 1000);
    this._tickInterval = setInterval(() => {
      remaining -= 1;
      if (timerEl && remaining > 0) timerEl.textContent = remaining + 's';
    }, 1000);
    this._commitTimeout = setTimeout(() => {
      this._commitRemoval();
    }, this.STAGE_DURATION_MS);
  }

  _undoRemoval() {
    if (!this._stagedLine) return;
    CartAnalytics.push('cart_undo_clicked', {
      product_title: this._stagedTitle || '',
      variant_id: this._stagedVariantId || '',
    });
    this._stagedLine.classList.remove('is-staged-remove');
    if (this._inlineUndoStrip && this._inlineUndoStrip.parentElement) {
      this._inlineUndoStrip.remove();
    }
    this._inlineUndoStrip = null;
    if (this._currentMode === 'removed') this._hide();
    this._cancelStaged();
  }

  _commitRemoval() {
    if (!this._stagedLine) return;
    CartAnalytics.push('cart_item_remove_committed', {
      product_title: this._stagedTitle || '',
      variant_id: this._stagedVariantId || '',
    });
    const commit = this._stagedCommit;
    if (this._inlineUndoStrip && this._inlineUndoStrip.parentElement) {
      this._inlineUndoStrip.remove();
    }
    this._inlineUndoStrip = null;
    if (this._currentMode === 'removed') this._hide();
    this._cancelStaged();
    if (typeof commit === 'function') commit();
  }

  _escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = String(str || '');
    return div.innerHTML;
  }

  _cancelStaged() {
    if (this._commitTimeout) clearTimeout(this._commitTimeout);
    if (this._tickInterval) clearInterval(this._tickInterval);
    // Also clean up the injected DOM strip — normally _undoRemoval /
    // _commitRemoval do this, but disconnectedCallback hits _cancelStaged
    // directly without going through those paths.
    if (this._inlineUndoStrip && this._inlineUndoStrip.parentElement) {
      this._inlineUndoStrip.remove();
    }
    this._inlineUndoStrip = null;
    this._commitTimeout = null;
    this._tickInterval = null;
    this._stagedLine = null;
    this._stagedCommit = null;
  }

  _show(mode) {
    this._currentMode = mode;
    this.setAttribute('data-toast-mode', mode);
    this.classList.add('is-visible');
  }

  _hide() {
    this._currentMode = null;
    this.removeAttribute('data-toast-mode');
    this.classList.remove('is-visible');
  }
}
customElements.define('cart-drawer-toast', CartDrawerToast);

/**
 * CartDrawerRecs — Shopify Search & Discovery seeded recommendations for
 * the empty-cart state. Reads the user's most-recent product ID from
 * `hypertheme:recently-viewed` localStorage and uses it as a seed for
 * `/recommendations/products.json?product_id=X&intent=related`. Renders
 * the returned products as horizontal-scroll cards matching the
 * `.cart-drawer__empty-featured` pattern.
 *
 * Behavior matrix:
 *   - localStorage has seed + fetch returns products  →  render cards
 *   - localStorage empty                              →  remove self
 *                                                        (static fallback
 *                                                        Liquid renders)
 *   - fetch fails / returns empty                     →  remove self
 *
 * The static `collections.frontpage` fallback in cart-drawer.liquid sits
 * AFTER this element in the DOM, so when this element removes itself,
 * the Liquid-rendered fallback is the visible result.
 *
 * Markup contract:
 *   <cart-drawer-recs class="cart-drawer__empty-recs" data-limit="8">
 *     <p class="cart-drawer__empty-tier-label">You may also like</p>
 *     <ul class="cart-drawer__empty-featured" data-recs-container></ul>
 *   </cart-drawer-recs>
 */
class CartDrawerRecs extends HTMLElement {
  constructor() {
    super();
    this._limit = parseInt(this.getAttribute('data-limit') || '8', 10);
  }

  connectedCallback() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this._init(), { timeout: 1500 });
    } else {
      setTimeout(() => this._init(), 250);
    }
  }

  _init() {
    const seedId = this._getSeedProductId();
    if (!seedId) {
      this.remove();
      return;
    }
    this._fetchRecs(seedId);
  }

  _getSeedProductId() {
    try {
      const raw = window.localStorage && window.localStorage.getItem('hypertheme:recently-viewed');
      if (!raw) return null;
      const items = JSON.parse(raw);
      if (!Array.isArray(items) || !items.length) return null;
      // First item is the most-recent (recently-viewed-products.js semantics).
      // Items may be plain numeric IDs or objects with .id.
      const first = items[0];
      if (typeof first === 'number' || typeof first === 'string') return String(first);
      if (first && (first.id || first.product_id)) return String(first.id || first.product_id);
      return null;
    } catch (_e) {
      return null;
    }
  }

  _fetchRecs(seedId) {
    const url = '/recommendations/products.json?product_id=' + encodeURIComponent(seedId) +
                '&intent=related&limit=' + this._limit;
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(async (r) => {
        if (!r.ok) {
          // Separate HTTP error from empty result — 5xx is a global signal,
          // not "this user has no recs". Breadcrumb only (no captureMessage):
          // during a Shopify outage we'd otherwise spam Sentry with one event
          // per user per session. Breadcrumbs attach to any captured
          // exception in the same session, which is the canonical Sentry
          // pattern for ambient signals.
          CartAnalytics.breadcrumb('cart_drawer_recs_http_error', {
            status: r.status,
            seed: seedId,
          }, r.status >= 500 ? 'error' : 'warning');
          return null;
        }
        return r.json();
      })
      .then((data) => {
        const products = data && data.products;
        if (!Array.isArray(products) || products.length === 0) {
          this.remove();
          return;
        }
        this._render(products);
      })
      .catch((err) => {
        CartAnalytics.breadcrumb('cart_drawer_recs_fetch_failed', { error: err && err.message }, 'warning');
        this.remove();
      });
  }

  _render(products) {
    // Cart re-renders (Section Rendering API) can detach this element between
    // requestIdleCallback firing and fetch resolving. Writing to a detached
    // element no-ops visibly but still fires analytics — would inflate the
    // 'rendered' denominator for the empty-cart CTR funnel.
    if (!this.isConnected) {
      CartAnalytics.breadcrumb('cart_drawer_recs_render_on_detached', { count: products.length });
      return;
    }
    const container = this.querySelector('[data-recs-container]');
    if (!container) {
      this.remove();
      return;
    }
    const cards = products.map((p) => this._cardHtml(p)).join('');
    container.innerHTML = cards;
    this.setAttribute('data-loaded', '');
    const fallback = document.querySelector('[data-empty-fallback="frontpage"]');
    if (fallback) fallback.remove();
    CartAnalytics.push('empty_cart_recs_rendered', { count: products.length });
    container.querySelectorAll('a.cart-drawer__empty-featured-link').forEach((link, idx) => {
      link.addEventListener('click', () => {
        CartAnalytics.push('empty_cart_recs_clicked', {
          position: idx,
          href: link.getAttribute('href'),
        });
      }, { once: true });
    });
  }

  _cardHtml(p) {
    const title = this._escape(p.title || '');
    const url = this._escape(p.url || '#');
    const img = p.featured_image || (p.media && p.media[0] && p.media[0].src) || '';
    const imgSrc = img ? this._sizedImage(img, 240) : '';
    const imgSrc120 = img ? this._sizedImage(img, 120) : '';
    const price = this._formatMoney(p.price);
    const compareAt = p.compare_at_price && p.compare_at_price > p.price
      ? this._formatMoney(p.compare_at_price) : null;
    return (
      '<li class="cart-drawer__empty-featured-item">' +
        '<a href="' + url + '" class="cart-drawer__empty-featured-link">' +
          '<div class="cart-drawer__empty-featured-thumb">' +
            (imgSrc
              ? '<img src="' + this._escape(imgSrc) + '" ' +
                'srcset="' + this._escape(imgSrc120) + ' 120w, ' + this._escape(imgSrc) + ' 240w" ' +
                'sizes="120px" alt="' + title + '" width="120" height="120" loading="lazy">'
              : '') +
          '</div>' +
          '<span class="cart-drawer__empty-featured-title">' + title + '</span>' +
          '<span class="cart-drawer__empty-featured-price">' +
            (compareAt
              ? '<span class="cart-drawer__empty-featured-was">' + compareAt + '</span>'
              : '') +
            price +
          '</span>' +
        '</a>' +
      '</li>'
    );
  }

  _sizedImage(url, width) {
    // Shopify CDN images accept ?width=X for resizing. If a query already
    // exists, append; otherwise add. Strip _NNNxNNN. legacy size token.
    if (!url) return url;
    if (url.indexOf('?') !== -1) return url + '&width=' + width;
    return url + '?width=' + width;
  }

  _formatMoney(cents) {
    if (typeof cents !== 'number') {
      const parsed = parseInt(cents, 10);
      if (!Number.isFinite(parsed)) return '';
      cents = parsed;
    }
    const dollars = cents / 100;
    return '$' + dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  _escape(str) {
    const div = document.createElement('div');
    div.textContent = String(str == null ? '' : str);
    // The textContent->innerHTML round trip only encodes & < > — the result
    // still gets interpolated straight into double-quoted attributes below
    // (alt=, href=, src=) in this same class, so an unescaped `"` (routine
    // in product titles, e.g. `52" Ceiling Fan`) would break out of the
    // attribute. Escape quotes too; harmless as extra entities in the text
    // contexts this same value is also used in.
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
}
customElements.define('cart-drawer-recs', CartDrawerRecs);

/**
 * CartDrawerRecent — clean re-render of recently-viewed products inside the
 * empty cart. Bypasses the heavy pcard component (vendor "by Brand" line,
 * superscript-cents prices, price ranges, dividers) by fetching the
 * recently-viewed-products section as before, then parsing out just the
 * minimum (href, image, title, price) and re-rendering with the same
 * card template as CartDrawerRecs.
 *
 * Source of truth for IDs is still `hypertheme:recently-viewed` localStorage,
 * matching recently-viewed-products.js semantics (newest-first).
 */
class CartDrawerRecent extends HTMLElement {
  constructor() {
    super();
    this._limit = parseInt(this.getAttribute('data-limit') || '6', 10);
    this._sectionUrl = this.getAttribute('data-url') || '';
  }

  connectedCallback() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this._init(), { timeout: 1500 });
    } else {
      setTimeout(() => this._init(), 250);
    }
  }

  _init() {
    const ids = this._getRecentIds();
    if (!ids.length || !this._sectionUrl) {
      this.remove();
      return;
    }
    const query = ids.slice(0, this._limit).map((id) => 'id:' + id).join(' OR ');
    fetch(this._sectionUrl + encodeURIComponent(query))
      .then(async (r) => {
        if (!r.ok) {
          CartAnalytics.breadcrumb('cart_drawer_recent_http_error', {
            status: r.status,
          }, r.status >= 500 ? 'error' : 'warning');
          return null;
        }
        return r.text();
      })
      .then((html) => {
        if (!html) return this.remove();
        const products = this._parseProducts(html);
        if (!products.length) return this.remove();
        this._render(products);
      })
      .catch((err) => {
        CartAnalytics.breadcrumb('cart_drawer_recent_fetch_failed', { error: err && err.message }, 'warning');
        this.remove();
      });
  }

  _getRecentIds() {
    try {
      const raw = window.localStorage && window.localStorage.getItem('hypertheme:recently-viewed');
      if (!raw) return [];
      const items = JSON.parse(raw);
      if (!Array.isArray(items)) return [];
      return items
        .map((item) => {
          if (typeof item === 'number' || typeof item === 'string') return String(item);
          if (item && (item.id || item.product_id)) return String(item.id || item.product_id);
          return null;
        })
        .filter(Boolean);
    } catch (_e) {
      return [];
    }
  }

  _parseProducts(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const cards = doc.querySelectorAll('.product-card, [class*="product-card"]');
    const byHandle = new Map();
    cards.forEach((card) => {
      const link = card.querySelector('a[href*="/products/"]');
      if (!link) return;
      const url = link.getAttribute('href');
      const handle = (url.match(/\/products\/([^?#/]+)/) || [])[1];
      if (!handle || byHandle.has(handle)) return;
      const titleEl = card.querySelector('.product-card__title, [class*="title"]');
      const title = titleEl ? titleEl.textContent.trim().replace(/\s+/g, ' ') : '';
      const img = card.querySelector('img');
      const imgSrc = img ? (img.getAttribute('data-src') || img.getAttribute('src') || '') : '';
      // Price: the .f-price__sale > .f-price-item--sale element always contains
      // the *selling* price (sale price when on sale, regular otherwise) per
      // price.liquid. Prefer that specifically. Without it we'd land on
      // .f-price__regular which holds the compare-at when on sale —
      // actively misleading the customer. Tail fallbacks are for markup drift.
      const priceEl =
        card.querySelector('.f-price__sale .f-price-item--sale') ||
        card.querySelector('.f-price__regular') ||
        card.querySelector('.f-price');
      let price = priceEl ? priceEl.textContent.trim().replace(/\s+/g, ' ') : '';
      // Trim "from $X" wrappers down to just the lowest visible price token
      const priceMatch = price.match(/\$[\d,]+(?:\.\d{2})?/);
      if (priceMatch) price = priceMatch[0];
      byHandle.set(handle, { url, title, imgSrc, price });
    });
    // If the section returned HTML but we couldn't extract any products, that's
    // a strong signal Shopify changed the underlying card markup. Breadcrumb so
    // the next Sentry capture surfaces it.
    if (byHandle.size === 0 && cards.length > 0) {
      CartAnalytics.breadcrumb('cart_drawer_recent_parse_empty', {
        section_cards_found: cards.length,
      }, 'warning');
    }
    return Array.from(byHandle.values());
  }

  _render(products) {
    // Cart re-renders can detach this element between fetch dispatch and resolve.
    if (!this.isConnected) {
      CartAnalytics.breadcrumb('cart_drawer_recent_render_on_detached', { count: products.length });
      return;
    }
    const container = this.querySelector('[data-recent-container]');
    if (!container) return this.remove();
    container.innerHTML = products.map((p) => this._cardHtml(p)).join('');
    this.setAttribute('data-loaded', '');
    CartAnalytics.push('empty_cart_recent_rendered', { count: products.length });
    container.querySelectorAll('a.cart-drawer__empty-featured-link').forEach((link, idx) => {
      link.addEventListener('click', () => {
        CartAnalytics.push('empty_cart_recent_clicked', {
          position: idx,
          href: link.getAttribute('href'),
        });
      }, { once: true });
    });
  }

  _cardHtml(p) {
    const title = this._escape(p.title || '');
    const url = this._escape(p.url || '#');
    const price = this._escape(p.price || '');
    const imgSrc = p.imgSrc ? this._sizedImage(p.imgSrc, 240) : '';
    const imgSrc120 = p.imgSrc ? this._sizedImage(p.imgSrc, 120) : '';
    return (
      '<li class="cart-drawer__empty-featured-item">' +
        '<a href="' + url + '" class="cart-drawer__empty-featured-link">' +
          '<div class="cart-drawer__empty-featured-thumb">' +
            (imgSrc
              ? '<img src="' + this._escape(imgSrc) + '" ' +
                'srcset="' + this._escape(imgSrc120) + ' 120w, ' + this._escape(imgSrc) + ' 240w" ' +
                'sizes="120px" alt="' + title + '" width="120" height="120" loading="lazy">'
              : '') +
          '</div>' +
          '<span class="cart-drawer__empty-featured-title">' + title + '</span>' +
          (price ? '<span class="cart-drawer__empty-featured-price">' + price + '</span>' : '') +
        '</a>' +
      '</li>'
    );
  }

  _sizedImage(url, width) {
    if (!url) return url;
    // Strip any existing width param so we don't end up with width=600&width=240
    const clean = url.replace(/([?&])width=\d+&?/i, '$1').replace(/[?&]$/, '');
    if (clean.indexOf('?') !== -1) return clean + '&width=' + width;
    return clean + '?width=' + width;
  }

  _escape(str) {
    const div = document.createElement('div');
    div.textContent = String(str == null ? '' : str);
    // The textContent->innerHTML round trip only encodes & < > — the result
    // still gets interpolated straight into double-quoted attributes below
    // (alt=, href=, src=) in this same class, so an unescaped `"` (routine
    // in product titles, e.g. `52" Ceiling Fan`) would break out of the
    // attribute. Escape quotes too; harmless as extra entities in the text
    // contexts this same value is also used in.
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
}
customElements.define('cart-drawer-recent', CartDrawerRecent);

/**
 * CartDrawerWishlist — Tier 1 empty-cart fallback. Renders the customer's own
 * Swish/WishlistKing saved items with the same clean card template as
 * CartDrawerRecent/CartDrawerRecs. Source of truth is the WishlistKing
 * read-side API (`_apis.wishlist.loadWishlist`), which is per-user CRUD only
 * (no aggregate/popularity data — see custom-wishlist.js for the full-page
 * equivalent). If WishlistKing never loads, the customer isn't logged in, or
 * their wishlist is empty, the element removes itself so Tier 2
 * (recently-viewed) becomes the visible result.
 *
 *   <cart-drawer-wishlist
 *     class="cart-drawer__empty-tier"
 *     data-url="{{ routes.search_url }}?section_id=recently-viewed-products&type=product&q="
 *     data-limit="6"
 *   >
 *     <p class="cart-drawer__empty-tier-label">Saved for later</p>
 *     <ul class="cart-drawer__empty-featured" data-wishlist-container></ul>
 *   </cart-drawer-wishlist>
 */
class CartDrawerWishlist extends HTMLElement {
  constructor() {
    super();
    this._limit = parseInt(this.getAttribute('data-limit') || '6', 10);
    this._sectionUrl = this.getAttribute('data-url') || '';
  }

  connectedCallback() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this._init(), { timeout: 1500 });
    } else {
      setTimeout(() => this._init(), 250);
    }
  }

  async _init() {
    const ids = await this._getWishlistIds();
    if (!ids.length || !this._sectionUrl) {
      this.remove();
      return;
    }
    const query = ids.slice(0, this._limit).map((id) => 'id:' + id).join(' OR ');
    fetch(this._sectionUrl + encodeURIComponent(query))
      .then(async (r) => {
        if (!r.ok) {
          CartAnalytics.breadcrumb('cart_drawer_wishlist_http_error', {
            status: r.status,
          }, r.status >= 500 ? 'error' : 'warning');
          return null;
        }
        return r.text();
      })
      .then((html) => {
        if (!html) return this.remove();
        const products = this._parseProducts(html);
        if (!products.length) return this.remove();
        this._render(products);
      })
      .catch((err) => {
        CartAnalytics.breadcrumb('cart_drawer_wishlist_fetch_failed', { error: err && err.message }, 'warning');
        this.remove();
      });
  }

  async _getWishlistIds() {
    const wishlistKing = await this._waitForWishlistKing();
    if (!wishlistKing) return [];
    try {
      // A hung loadWishlist() promise (backend stall) would otherwise leave
      // this tier's :not([data-loaded]) CSS rule hiding an empty label
      // indefinitely instead of falling through to Tier 2. Race it against a
      // timeout so a stall degrades to "no wishlist" instead of "stuck".
      const timeout = new Promise((_resolve, reject) => {
        setTimeout(() => reject(new Error('loadWishlist timed out')), 5000);
      });
      const { wishlist } = await Promise.race([
        wishlistKing._apis.wishlist.loadWishlist({
          wishlistId: 'mine',
          cacheStrategy: 'no-store',
        }),
        timeout,
      ]);
      const items = wishlist?.items || [];
      const seen = new Set();
      const ids = [];
      for (const item of items) {
        const id = item.product?.id || item.productId || item.product_id;
        if (id != null && !seen.has(id)) {
          seen.add(id);
          ids.push(String(id));
        }
      }
      return ids;
    } catch (err) {
      CartAnalytics.breadcrumb('cart_drawer_wishlist_load_failed', { error: err && err.message }, 'warning');
      return [];
    }
  }

  _waitForWishlistKing() {
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 20;
      const check = () => {
        attempts++;
        if (window.WishlistKing?._apis?.wishlist) {
          resolve(window.WishlistKing);
          return;
        }
        if (attempts < maxAttempts) {
          setTimeout(check, 250);
        } else {
          resolve(null);
        }
      };
      check();
    });
  }

  _parseProducts(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const cards = doc.querySelectorAll('.product-card, [class*="product-card"]');
    const byHandle = new Map();
    cards.forEach((card) => {
      const link = card.querySelector('a[href*="/products/"]');
      if (!link) return;
      const url = link.getAttribute('href');
      const handle = (url.match(/\/products\/([^?#/]+)/) || [])[1];
      if (!handle || byHandle.has(handle)) return;
      const titleEl = card.querySelector('.product-card__title, [class*="title"]');
      const title = titleEl ? titleEl.textContent.trim().replace(/\s+/g, ' ') : '';
      const img = card.querySelector('img');
      const imgSrc = img ? (img.getAttribute('data-src') || img.getAttribute('src') || '') : '';
      const priceEl =
        card.querySelector('.f-price__sale .f-price-item--sale') ||
        card.querySelector('.f-price__regular') ||
        card.querySelector('.f-price');
      let price = priceEl ? priceEl.textContent.trim().replace(/\s+/g, ' ') : '';
      const priceMatch = price.match(/\$[\d,]+(?:\.\d{2})?/);
      if (priceMatch) price = priceMatch[0];
      byHandle.set(handle, { url, title, imgSrc, price });
    });
    if (byHandle.size === 0 && cards.length > 0) {
      CartAnalytics.breadcrumb('cart_drawer_wishlist_parse_empty', {
        section_cards_found: cards.length,
      }, 'warning');
    }
    return Array.from(byHandle.values());
  }

  _render(products) {
    if (!this.isConnected) {
      CartAnalytics.breadcrumb('cart_drawer_wishlist_render_on_detached', { count: products.length });
      return;
    }
    const container = this.querySelector('[data-wishlist-container]');
    if (!container) return this.remove();
    container.innerHTML = products.map((p) => this._cardHtml(p)).join('');
    this.setAttribute('data-loaded', '');
    CartAnalytics.push('empty_cart_wishlist_rendered', { count: products.length });
    container.querySelectorAll('a.cart-drawer__empty-featured-link').forEach((link, idx) => {
      link.addEventListener('click', () => {
        CartAnalytics.push('empty_cart_wishlist_clicked', {
          position: idx,
          href: link.getAttribute('href'),
        });
      }, { once: true });
    });
  }

  _cardHtml(p) {
    const title = this._escape(p.title || '');
    const url = this._escape(p.url || '#');
    const price = this._escape(p.price || '');
    const imgSrc = p.imgSrc ? this._sizedImage(p.imgSrc, 240) : '';
    const imgSrc120 = p.imgSrc ? this._sizedImage(p.imgSrc, 120) : '';
    return (
      '<li class="cart-drawer__empty-featured-item">' +
        '<a href="' + url + '" class="cart-drawer__empty-featured-link">' +
          '<div class="cart-drawer__empty-featured-thumb">' +
            (imgSrc
              ? '<img src="' + this._escape(imgSrc) + '" ' +
                'srcset="' + this._escape(imgSrc120) + ' 120w, ' + this._escape(imgSrc) + ' 240w" ' +
                'sizes="120px" alt="' + title + '" width="120" height="120" loading="lazy">'
              : '') +
          '</div>' +
          '<span class="cart-drawer__empty-featured-title">' + title + '</span>' +
          (price ? '<span class="cart-drawer__empty-featured-price">' + price + '</span>' : '') +
        '</a>' +
      '</li>'
    );
  }

  _sizedImage(url, width) {
    if (!url) return url;
    const clean = url.replace(/([?&])width=\d+&?/i, '$1').replace(/[?&]$/, '');
    if (clean.indexOf('?') !== -1) return clean + '&width=' + width;
    return clean + '?width=' + width;
  }

  _escape(str) {
    const div = document.createElement('div');
    div.textContent = String(str == null ? '' : str);
    // The textContent->innerHTML round trip only encodes & < > — the result
    // still gets interpolated straight into double-quoted attributes below
    // (alt=, href=, src=) in this same class, so an unescaped `"` (routine
    // in product titles, e.g. `52" Ceiling Fan`) would break out of the
    // attribute. Escape quotes too; harmless as extra entities in the text
    // contexts this same value is also used in.
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
}
customElements.define('cart-drawer-wishlist', CartDrawerWishlist);

/**
 * CartShippingEstimate — native (no Worker) shipping estimate inside the
 * cart drawer footer.
 *
 * Destination resolution priority:
 *   1. cart.attributes.destination_zip  (persisted on the cart object)
 *   2. customer.default_address.zip     (logged-in fallback, gets persisted)
 *   3. none                              → free-ship progress only
 *
 * When a customer-level address exists but the cart doesn't have it yet,
 * the element POSTs to /cart/update.js to persist the destination into
 * cart.attributes — so subsequent renders (and the cart page) see it
 * even after the customer logs out within the same cart session.
 *
 * Once a destination is known, the element calls /cart/shipping_rates.json
 * and renders the cheapest rate. Below the free-ship threshold, it also
 * shows the gap ("$X.XX — add $Y.YY for FREE"). Above threshold the row
 * stays FREE (server-rendered).
 *
 * No external dependencies, no Cloudflare Worker, no third-party IP geo.
 * Only sources are Shopify-native: cart.attributes, customer.default_address.
 */
class CartShippingEstimate extends HTMLElement {
  constructor() {
    super();
    this._cartZip = this.dataset.cartZip || '';
    this._cartState = this.dataset.cartState || '';
    this._cartCountry = this.dataset.cartCountry || 'US';
    this._customerZip = this.dataset.customerZip || '';
    this._customerState = this.dataset.customerState || '';
    this._customerCountry = this.dataset.customerCountry || 'US';
    this._freeThresholdCents = parseInt(this.dataset.freeThresholdCents || '0', 10);
    this._cartTotalCents = parseInt(this.dataset.cartTotalCents || '0', 10);
    this._hasFreeShipping = this.dataset.hasFreeShipping === 'true';
  }

  connectedCallback() {
    // Server already rendered FREE — only thing we might add is a "free shipping" pill,
    // but the row is already correct. Skip hydration.
    if (this._hasFreeShipping) return;

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this._hydrate(), { timeout: 1500 });
    } else {
      setTimeout(() => this._hydrate(), 250);
    }
  }

  async _hydrate() {
    const dest = this._resolveDestination();

    // Always show free-shipping progress as a baseline (works with or without dest).
    this._renderFreeShipProgress();

    if (!dest) return; // No destination — progress message is the final state.

    // Persist customer destination into cart.attributes on first encounter so
    // subsequent renders and the cart page see it without re-reading customer.
    // Defensive .catch() — _persistToCart handles errors internally, but if a
    // future refactor introduces a synchronous throw before the first await
    // it'd otherwise produce an unhandled rejection.
    if (dest.source === 'customer') {
      this._persistToCart(dest).catch(() => {});
    }

    // Fetch rates and replace the value if we got a useful answer.
    try {
      const rates = await this._fetchRates(dest);
      if (rates && rates.length) {
        const cheapest = rates.reduce((a, b) => parseFloat(a.price) < parseFloat(b.price) ? a : b);
        this._renderRate(cheapest, dest);
        CartAnalytics.push('cart_shipping_estimate_resolved', {
          destination_source: dest.source,
          destination_zip: dest.zip,
          destination_state: dest.state || '',
          rate_price_cents: Math.round(parseFloat(cheapest.price) * 100),
          rate_name: cheapest.name || '',
        });
      }
    } catch (err) {
      CartAnalytics.breadcrumb('cart_shipping_rates_fetch_failed', {
        error: err && err.message,
        destination_zip: dest.zip,
      }, 'warning');
      // Leave the free-ship-progress fallback in place.
    }
  }

  _resolveDestination() {
    if (this._cartZip) {
      return { zip: this._cartZip, state: this._cartState, country: this._cartCountry, source: 'cart' };
    }
    if (this._customerZip) {
      return { zip: this._customerZip, state: this._customerState, country: this._customerCountry, source: 'customer' };
    }
    return null;
  }

  async _persistToCart(dest) {
    // Best-effort write to cart attributes — failures don't block this session's
    // rates fetch, but we want observability so a 422 (validation regression,
    // app conflict) doesn't go undetected across the whole logged-in customer
    // base. Breadcrumb both HTTP errors and thrown errors.
    if (this._persistAttempted) return;
    this._persistAttempted = true;
    const body = JSON.stringify({
      attributes: {
        destination_zip: dest.zip,
        destination_state: dest.state || '',
        destination_country: dest.country || 'US',
      },
    });
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });
      if (!res.ok) {
        CartAnalytics.breadcrumb('cart_destination_persist_http_error', {
          status: res.status,
          zip: dest.zip,
          source: dest.source,
        }, 'warning');
      }
    } catch (err) {
      CartAnalytics.breadcrumb('cart_destination_persist_threw', {
        error: err && err.message,
        zip: dest.zip,
      }, 'warning');
    }
  }

  async _fetchRates(dest) {
    // Per-cart-state cache so a multi-line cart only hits the API once per
    // session — BUT the key includes cart_total_cents + item_count so adding
    // an item invalidates the cache (Shopify rates can depend on subtotal,
    // weight, qty; serving a stale rate after add-to-cart misleads the buyer
    // and erodes checkout trust).
    const cacheKey = 'shop-theme:ship-rates:' +
      (this._cartTokenFromCookie() || 'anon') + ':' +
      dest.zip + ':' +
      this._cartTotalCents + ':' +
      ((CartAnalytics._baseProps && CartAnalytics._baseProps().cart_item_count) || 0);
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.ts && (Date.now() - parsed.ts) < 5 * 60 * 1000) {
          this._lastFetchFromCache = true;
          return parsed.rates;
        }
      }
    } catch (_e) { /* sessionStorage disabled — fine */ }
    this._lastFetchFromCache = false;

    const params = new URLSearchParams({
      'shipping_address[zip]': dest.zip,
      'shipping_address[country]': dest.country || 'US',
    });
    if (dest.state) params.set('shipping_address[province]', dest.state);

    const res = await fetch('/cart/shipping_rates.json?' + params.toString(), {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      // Breadcrumb only. Don't captureMessage on 5xx — during a Shopify
      // shipping-API outage we'd otherwise blow daily Sentry quota with one
      // event per user per session. Breadcrumbs surface on any captured
      // exception in the session, which is sufficient signal.
      CartAnalytics.breadcrumb('cart_shipping_rates_http_error', {
        status: res.status,
        zip: dest.zip,
      }, res.status >= 500 ? 'error' : 'warning');
      return null;
    }
    const data = await res.json();
    const rates = (data && data.shipping_rates) || [];

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), rates }));
    } catch (_e) { /* fine */ }

    return rates;
  }

  _renderRate(rate, dest) {
    const valueEl = this.querySelector('[data-shipping-value]');
    if (!valueEl) return;
    const price = parseFloat(rate.price);
    const priceText = price === 0 ? 'FREE' : this._formatMoney(Math.round(price * 100));
    const stateSuffix = dest.state ? ' to ' + this._escape(dest.state) : '';
    valueEl.classList.remove('cart-drawer__totals-placeholder');

    // When below the free-ship threshold, the actionable gap message
    // ("Add $11.02 for FREE") is the highest-CRO-value content for this row.
    // Show the rate alongside it rather than overwriting it.
    const belowThreshold = this._freeThresholdCents > 0
      && this._cartTotalCents < this._freeThresholdCents;
    if (belowThreshold) {
      const gapCents = this._freeThresholdCents - this._cartTotalCents;
      const gapText = this._formatMoney(gapCents);
      valueEl.textContent = priceText + stateSuffix + ' · Add ' + gapText + ' for FREE';
    } else {
      valueEl.textContent = priceText + stateSuffix;
    }
  }

  _renderFreeShipProgress() {
    if (this._freeThresholdCents <= 0) return;
    if (this._cartTotalCents >= this._freeThresholdCents) return; // already free
    const valueEl = this.querySelector('[data-shipping-value]');
    if (!valueEl) return;
    const gapCents = this._freeThresholdCents - this._cartTotalCents;
    const gapText = this._formatMoney(gapCents);
    // Replace the static "Calculated at checkout" with the actionable gap message.
    valueEl.classList.remove('cart-drawer__totals-placeholder');
    valueEl.textContent = 'Add ' + gapText + ' for FREE';
  }

  _cartTokenFromCookie() {
    const m = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
    return m ? m[1] : '';
  }

  _formatMoney(cents) {
    const dollars = cents / 100;
    return '$' + dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  _escape(str) {
    const div = document.createElement('div');
    div.textContent = String(str == null ? '' : str);
    // The textContent->innerHTML round trip only encodes & < > — the result
    // still gets interpolated straight into double-quoted attributes below
    // (alt=, href=, src=) in this same class, so an unescaped `"` (routine
    // in product titles, e.g. `52" Ceiling Fan`) would break out of the
    // attribute. Escape quotes too; harmless as extra entities in the text
    // contexts this same value is also used in.
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
}
customElements.define('cart-shipping-estimate', CartShippingEstimate);

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', FoxTheme.utils.debounce(this.onChange.bind(this), 300));

    if (this.button && this.cartNoteDetailsSummary) {
      this.button.addEventListener('click', () => {
        this.cartNoteDetailsSummary.close();
      });
    }
  }

  get cartNoteDetailsSummary() {
    return this.closest('[is="accordion-details"]');
  }

  get button() {
    return this.querySelector('[type="button"]');
  }

  onChange(event) {
    const body = JSON.stringify({ note: event.target.value });
    fetch(`${FoxTheme.routes.cart_update_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } });
  }
}
customElements.define('cart-note', CartNote);

class CartDiscount extends HTMLFormElement {
  constructor() {
    super();

    this.addEventListener('submit', this.handleFormSubmit.bind(this));
  }

  get submitEl() {
    return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
  }

  get messageEl() {
    return (this._messageEl = this._messageEl || this.querySelector('.form__message'));
  }

  get couponEl() {
    return (this._couponEl = this._couponEl || this.querySelector('input[name="discount"]'));
  }

  get cartAddonDrawer() {
    return this.closest('.cart-addons-drawer');
  }

  get cartDiscountsEl() {
    return document.querySelector('.cart__discounts');
  }

  getDiscounts() {
    const discounts = [];

    if (this.cartDiscountsEl) {
      const items = this.cartDiscountsEl.querySelectorAll('.discount');
      items &&
        items.forEach((item) => {
          discounts.push(item.dataset.discountCode);
        });
    }

    return discounts;
  }

  handleFormSubmit(event) {
    event.preventDefault();

    if (this.submitEl.getAttribute('aria-disabled') === 'true') return;

    this.displayFormErrors();

    const newDiscountCode = this.couponEl.value;
    const discounts = this.getDiscounts();

    if (discounts.includes(newDiscountCode)) {
      this.displayFormErrors(FoxTheme.cartStrings.duplicateDiscountError);
      return;
    }

    discounts.push(newDiscountCode);

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const config = FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    formData.append('discount', discounts.join(','));

    config.body = formData;

    this.submitEl.setAttribute('aria-disabled', 'true');
    this.submitEl.classList.add('btn--loading');

    fetch(FoxTheme.routes.cart_update_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (
          parsedState.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === newDiscountCode && discount.applicable === false;
          })
        ) {
          this.couponEl.value = '';
          this.displayFormErrors(FoxTheme.cartStrings.applyDiscountError);
          return;
        }

        if (this.cartAddonDrawer) {
          this.cartAddonDrawer.hide();
        }

        const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
        cartJson['sections'] = parsedState['sections'];

        this.updateCartState(cartJson);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      });
  }

  updateCartState = (cartJson) => {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
  };

  displayFormErrors = (errorMessage = false) => {
    if (!this.messageEl) {
      if (errorMessage !== false) {
        alert(errorMessage);
      }
    } else {
      this.messageEl.classList.toggle('!hidden', !errorMessage);
      if (errorMessage !== false) {
        this.messageEl.innerText = errorMessage;
      }
    }
  };
}
customElements.define('cart-discount', CartDiscount, { extends: 'form' });

class CartDiscountRemove extends HTMLButtonElement {
  constructor() {
    super();

    this.selectors = {
      list: '.cart__discounts',
      item: '.discount',
    };

    this.clickHandler = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.listEl = this.closest(this.selectors.list);

    this.addEventListener('click', this.clickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.clickHandler);
  }

  handleClick(event) {
    event.preventDefault();
    if (this.getAttribute('aria-disabled') === 'true') return;

    // listEl may be null if the button wasn't inside .cart__discounts at connect
    // time (re-rendered cart markup); re-resolve and bail rather than throw.
    const listEl = this.listEl || this.closest(this.selectors.list);
    if (!listEl) return;

    this.setAttribute('aria-disabled', 'true');
    this.classList.add('btn--loading');

    this.discounts = [];

    // Reuse `selectors.item` rather than hardcoding 'li' — callers render a
    // discount row as whatever element fits their markup (main-cart.liquid
    // uses <li>, cart-drawer.liquid uses <div>); both just need the same
    // '.discount' class this selector already looks for below.
    const thisItem = this.closest(this.selectors.item);
    const items = listEl.querySelectorAll(this.selectors.item);
    items &&
      items.forEach((item) => {
        if (item !== thisItem) {
          this.discounts.push(item.dataset.discountCode);
        }
      });

    this.updateCartDiscounts();
  }

  updateCartDiscounts() {
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const config = FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    formData.append('discount', this.discounts.join(','));

    config.body = formData;

    fetch(FoxTheme.routes.cart_update_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
        cartJson['sections'] = parsedState['sections'];

        this.updateCartState(cartJson);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  updateCartState = (cartJson) => {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
  };
}
customElements.define('cart-discount-remove', CartDiscountRemove, { extends: 'button' });

class CalculateShipping extends CartAddonModal {
  constructor() {
    super();
    this.countryProvince = this.querySelector('country-province');
    this.isCountrySetup = false;
  }
  static get observedAttributes() {
    return [...super.observedAttributes, 'data-show'];
  }
  show() {
    super.show();
    this.setAttribute('data-show', true);
  }

  hide() {
    super.hide();
    this.setAttribute('data-show', false);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === 'data-show' && newValue === 'true' && !this.isCountrySetup && this.countryProvince) {
      const template = this.countryProvince.querySelector('template');
      const templateContent = document.importNode(template.content, true);
      this.countryProvince.countryElement.appendChild(templateContent);
      this.countryProvince.init();
      this.isCountrySetup = true;
    }
  }
}
customElements.define('calculate-shipping', CalculateShipping);
class CountryProvinceForm extends HTMLElement {
  constructor() {
    super();
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.template = this.dataset.template;
    this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));
    if (this.template && this.template === 'cart') {
      this.init();
    }
  }

  init() {
    if (this.getAttribute('country') !== '') {
      this.countryElement.selectedIndex = Math.max(
        0,
        Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.dataset.country)
      );
      this.countryElement.dispatchEvent(new Event('change'));
    } else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option = this.countryElement.options[this.countryElement.selectedIndex],
      provinces = JSON.parse(option.dataset.provinces);

    this.provinceElement.closest('.form-field').hidden = provinces.length === 0;

    if (provinces.length === 0) {
      return;
    }

    this.provinceElement.innerHTML = '';

    provinces.forEach((data) => {
      const selected = data[1] === this.dataset.province;
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
}
customElements.define('country-province', CountryProvinceForm);

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;

    // Add null check to prevent errors if submit button doesn't exist
    if (this.submitButton) {
      this.submitButton.addEventListener('click', this.handleFormSubmit.bind(this));
    }
  }

  handleFormSubmit(event) {
    event.preventDefault();

    // Add null checks to prevent "Cannot read property 'value' of null" errors
    const zipEl = this.querySelector('[name="address[zip]"]');
    const countryEl = this.querySelector('[name="address[country]"]');
    const provinceEl = this.querySelector('[name="address[province]"]');

    const zip = zipEl?.value || '';
    const country = countryEl?.value || '';
    const province = provinceEl?.value || '';

    if (this.submitButton) {
      this.submitButton.classList.add('btn--loading');
    }

    const body = JSON.stringify({
      shipping_address: { zip, country, province },
    });
    let sectionUrl = `${FoxTheme.routes.cart_url}/shipping_rates.json`;

    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...FoxTheme.utils.fetchConfig('javascript'), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        } else {
          this.formatError(parsedState);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        if (this.resultsElement) this.resultsElement.hidden = false;
        if (this.submitButton) this.submitButton.classList.remove('btn--loading');
      });
  }

  formatError(errors) {
    const shippingRatesList = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert alert--error blocks-radius grid gap-2">
        <p class="font-body-bolder m-0">${FoxTheme.shippingCalculatorStrings.error}</p>
        <ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>
      </div>
    `;
  }

  formatShippingRates(shippingRates) {
    const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
      return `<li>${presentment_name}: ${currency} ${price}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert blocks-radius alert--${
        shippingRates.length === 0 ? 'error' : 'success'
      } grid gap-2 leading-tight">
        <p class="font-body-bolder m-0">${
          shippingRates.length === 0
            ? FoxTheme.shippingCalculatorStrings.notFound
            : shippingRates.length === 1
            ? FoxTheme.shippingCalculatorStrings.oneResult
            : FoxTheme.shippingCalculatorStrings.multipleResults
        }</p>
        ${
          shippingRatesList === ''
            ? ''
            : `<ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>`
        }
      </div>
    `;
  }
}
customElements.define('shipping-calculator', ShippingCalculator, { extends: 'form' });

class CartDrawerProductsRecommendation extends HTMLElement {
  constructor() {
    super();
    this.carousel = false;
    this.isLoading = false;

    this.classes = {
      grid: 'f-grid',
      swiper: 'swiper',
      swiperWrapper: 'swiper-wrapper',
      loading: 'is-loading',
    };

    // Handle Safari separately
    const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
    if (isSafari) {
      this.init();
    } else {
      FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
    }
  }

  disconnectedCallback() {
    // Cleanup
    this.destroyCarousel();
    const mql = window.matchMedia(FoxTheme.config?.mediaQueryMobile ?? 'screen and (max-width: 767px)');
    mql.removeEventListener('change', this.handleCarousel.bind(this));
  }

  init() {
    if (this.isLoading || !this.dataset.url) return;

    this.isLoading = true;
    this.classList.add(this.classes.loading);

    fetch(this.dataset.url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if (!sectionInnerHTML) return;

        const recommendations = sectionInnerHTML.querySelector('cart-drawer-products-recommendation');
        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          const mql = window.matchMedia(FoxTheme.config?.mediaQueryMobile ?? 'screen and (max-width: 767px)');
          mql.addEventListener('change', this.handleCarousel.bind(this));
          this.handleCarousel();
          this.dispatchEvent(new CustomEvent('recommendations:loaded'));
        } else {
          this.closest('.shopify-section')?.remove();
        }
      })
      .catch((error) => {
        console.error('Error loading recommendations:', error);
        this.classList.add('has-error');
      })
      .finally(() => {
        this.isLoading = false;
        this.classList.remove(this.classes.loading);
      });
  }

  initCarousel() {
    if (!this.wrapper?.innerHTML.trim().length) {
      this.classList.add('hidden');
      return;
    }

    if (typeof this.carousel !== 'object') {
      try {
        this.container.classList.add(this.classes.swiper);
        this.wrapper.classList.remove(this.classes.grid);
        this.wrapper.classList.add(this.classes.swiperWrapper);

        let options = {
          slidesPerView: this.columns,
          spaceBetween: 8,
          loop: false,
          navigation: {
            nextEl: this.sliderNext,
            prevEl: this.sliderPrev,
          },
        };

        const paginationEl = this.querySelector('.swiper-pagination');
        if (paginationEl) {
          options = {
            ...options,
            pagination: {
              el: paginationEl,
              clickable: true,
              type: 'fraction',
            },
          };
        }

        this.carousel = new FoxTheme.Carousel(this.slideContainer, options);

        if (this.carousel) {
          this.carousel.init();
          this.calcNavButtonsPosition();
        }
      } catch (error) {
        console.error('Error initializing carousel:', error);
        this.destroyCarousel();
      }
    }
  }

  handleCarousel() {
    const containerEl = this.container;
    this.columns = containerEl.dataset.columns;
    this.enableSliderMobile = containerEl.dataset.enableSliderMobile === 'true';

    if (FoxTheme.config.mqlMobile) {
      this.enableSliderMobile ? this.initCarousel() : this.destroyCarousel();
    } else {
      this.initCarousel();
    }
  }

  destroyCarousel() {
    if (!this.wrapper?.innerHTML.trim().length) {
      this.classList.add('hidden');
      return;
    }
    this.container?.classList.remove(this.classes.swiper);
    this.wrapper?.classList.remove(this.classes.swiperWrapper);
    this.wrapper?.classList.add(this.classes.grid);

    if (typeof this.sliderInstance !== 'object') return;
    this.carousel.slider.destroy();
    this.carousel = false;
  }

  calcNavButtonsPosition() {
    if (!this.dataset.calcButtonPosition === 'true') return;
    const firstMedia = this.querySelector('.product-card__image-wrapper');
    if (firstMedia && firstMedia.clientHeight > 0) {
      this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
    }
  }

  get slideContainer() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get container() {
    return this.querySelector('.cart-drawer-products-recommendation__container');
  }

  get wrapper() {
    return this.querySelector('.cart-drawer-products-recommendation__wrapper');
  }
}
customElements.define('cart-drawer-products-recommendation', CartDrawerProductsRecommendation);

class MainCart extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('cart:grouped-sections', this.getSectionToRender.bind(this));
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }
}
customElements.define('main-cart', MainCart);

class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      leftToSpend: '[data-left-to-spend]',
      warningInternational: '.free-shipping-goal__warning--international',
      warningRemote: '.free-shipping-goal__warning--remote',
      defaultContent: '.free-shipping-goal__default-content',
    };
    this.goal = Number(this.dataset.minimumAmount) * Number(window.Shopify.currency.rate || 1) || 0;
    this.progress = this.querySelector('progress-bar');
    this.money_format = window.FoxTheme.settings.moneyFormat;

    // Detect shipping location
    this.shippingLocation = this.detectShippingLocation();
  }

  /**
   * Detect user's likely location based on timezone
   * Returns: 'continental-us', 'hawaii', 'alaska', 'us-territory', 'international'
   */
  detectShippingLocation() {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Hawaii
      if (timezone === 'Pacific/Honolulu') {
        return 'hawaii';
      }

      // Alaska timezones
      const alaskaTimezones = [
        'America/Anchorage',
        'America/Juneau',
        'America/Sitka',
        'America/Yakutat',
        'America/Nome',
        'America/Metlakatla',
        'America/Adak'
      ];
      if (alaskaTimezones.includes(timezone)) {
        return 'alaska';
      }

      // US Territories
      const territoryTimezones = [
        'Pacific/Guam',           // Guam
        'Pacific/Saipan',         // Northern Mariana Islands
        'America/Puerto_Rico',    // Puerto Rico
        'America/Virgin',         // US Virgin Islands
        'Pacific/Pago_Pago',      // American Samoa
      ];
      if (territoryTimezones.includes(timezone)) {
        return 'us-territory';
      }

      // Continental US timezones
      const usTimezones = [
        'America/New_York',
        'America/Detroit',
        'America/Kentucky/Louisville',
        'America/Kentucky/Monticello',
        'America/Indiana/Indianapolis',
        'America/Indiana/Vincennes',
        'America/Indiana/Winamac',
        'America/Indiana/Marengo',
        'America/Indiana/Petersburg',
        'America/Indiana/Vevay',
        'America/Chicago',
        'America/Indiana/Tell_City',
        'America/Indiana/Knox',
        'America/Menominee',
        'America/North_Dakota/Center',
        'America/North_Dakota/New_Salem',
        'America/North_Dakota/Beulah',
        'America/Denver',
        'America/Boise',
        'America/Phoenix',
        'America/Los_Angeles',
      ];
      if (usTimezones.includes(timezone)) {
        return 'continental-us';
      }

      // If timezone starts with America/ but not in our list, might be Canada/Mexico/South America
      // If timezone starts with Europe/, Asia/, Africa/, etc. - definitely international
      if (!timezone.startsWith('America/')) {
        return 'international';
      }

      // Default to international for unknown America/ timezones (Canada, Mexico, etc.)
      return 'international';
    } catch (e) {
      // If timezone detection fails, assume continental US
      console.warn('[FreeShippingGoal] Timezone detection failed:', e);
      return 'continental-us';
    }
  }

  connectedCallback() {
    // Show appropriate warning based on location
    this.updateLocationWarning();

    this.updateShippingGloal(Number(this.dataset.cartTotal));
    document.addEventListener('cart:updated', (event) => {
      this.updateShippingGloal(event.detail.cart.items_subtotal_price);
    });
  }

  updateLocationWarning() {
    const internationalWarning = this.querySelector(this.selectors.warningInternational);
    const remoteWarning = this.querySelector(this.selectors.warningRemote);
    const defaultContent = this.querySelector(this.selectors.defaultContent);

    // Hide all warnings by default
    if (internationalWarning) internationalWarning.style.display = 'none';
    if (remoteWarning) remoteWarning.style.display = 'none';
    if (defaultContent) defaultContent.style.display = '';

    switch (this.shippingLocation) {
      case 'international':
        // Show international warning, hide free shipping content
        if (internationalWarning) internationalWarning.style.display = '';
        if (defaultContent) defaultContent.style.display = 'none';
        this.classList.add('free-shipping-goal--international');
        break;

      case 'hawaii':
      case 'alaska':
      case 'us-territory':
        // Show remote area warning alongside free shipping goal
        if (remoteWarning) {
          remoteWarning.style.display = '';
          // Update the location name in the warning
          const locationName = this.shippingLocation === 'hawaii' ? 'Hawaii'
            : this.shippingLocation === 'alaska' ? 'Alaska'
            : 'US territories';
          const locationSpan = remoteWarning.querySelector('[data-location-name]');
          if (locationSpan) locationSpan.textContent = locationName;
        }
        this.classList.add('free-shipping-goal--remote');
        break;

      case 'continental-us':
      default:
        // No warnings needed
        break;
    }
  }

  updateShippingGloal(amount) {
    // Don't show progress for international users
    if (this.shippingLocation === 'international') {
      return;
    }

    if (amount > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    this.cartTotal = amount / 100;
    this.goalLeft = this.goal - this.cartTotal;
    this.goalDone = this.goalLeft <= 0;

    this.percent = (this.cartTotal * 100) / this.goal;

    if (this.percent >= 100) this.percent = 100;

    if (this.cartTotal >= this.goal) {
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.classList.add('free-shipping-goal--done');
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    } else {
      const spend = (this.goal - this.cartTotal) * 100;
      this.querySelector(this.selectors.leftToSpend).innerHTML = FoxTheme.Currency.formatMoney(
        spend,
        this.money_format
      );
      this.classList.remove('free-shipping-goal--done');
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    }
  }
}
customElements.define('free-shipping-goal', FreeShippingGoal);

window.FoxKitAddToCart = async (payload) => {
  if (!payload?.properties?.['_FoxKit offer']) return;

  // Sentry SHOPIFY-1E1: unlike every other cart fetch in this file, this one
  // had no res.ok check and no catch -- callers (the FoxKit app's own script)
  // invoke this fire-and-forget, so a transient non-2xx from Shopify (503s
  // observed in breadcrumbs) threw on .json() as an unhandled rejection.
  try {
    const res = await fetch(`${FoxTheme.routes.cart_url}`, {
      ...FoxTheme.utils.fetchConfig(),
    });
    if (!res.ok) {
      console.error('FoxKitAddToCart: cart fetch failed', res.status);
      return;
    }
    const cartJson = await res.json();
    cartJson['sections'] = payload['sections'];
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

    document.dispatchEvent(
      new CustomEvent('product-ajax:added', {
        detail: {
          product: payload,
        },
      })
    );
  } catch (err) {
    console.error('FoxKitAddToCart error:', err);
  }
};
